// src/components/admin/compensation/FlaggedEmployeesPanel.js
// Panel for reviewing and resolving employees flagged during auto-assignment

import React, { useState, useEffect } from 'react';
import {
    AlertTriangle,
    XCircle,
    GitBranch,
    DollarSign,
    Briefcase,
    Building2,
    RefreshCw,
    CheckCircle
} from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import payGroupsService from '../../../services/payGroups.service';

const FlaggedEmployeesPanel = ({ onResolved }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';

    const [flaggedEmployees, setFlaggedEmployees] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [showResolutionModal, setShowResolutionModal] = useState(false);

    useEffect(() => {
        fetchFlaggedEmployees();
    }, []);

    const fetchFlaggedEmployees = async () => {
        setLoading(true);
        try {
            const response = await payGroupsService.getFlaggedEmployees();
            if (response.success) {
                setFlaggedEmployees(response.flagged_employees || []);
                setSummary(response.summary || {});
            }
        } catch (error) {
            console.error('Error fetching flagged employees:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleResolveClick = (employee) => {
        setSelectedEmployee(employee);
        setShowResolutionModal(true);
    };

    const handleResolutionComplete = () => {
        setShowResolutionModal(false);
        setSelectedEmployee(null);
        fetchFlaggedEmployees();
        if (onResolved) onResolved();
    };

    const getFlagIcon = (flagReason) => {
        switch (flagReason) {
            case 'conflict':
                return <GitBranch className="w-5 h-5 text-orange-500" />;
            case 'no_match':
                return <XCircle className="w-5 h-5 text-red-500" />;
            case 'out_of_range':
                return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
            default:
                return <AlertTriangle className="w-5 h-5 text-gray-500" />;
        }
    };

    const getFlagLabel = (flagReason) => {
        switch (flagReason) {
            case 'conflict':
                return 'Multiple Matches';
            case 'no_match':
                return 'No Match Found';
            case 'out_of_range':
                return 'Out of Range';
            default:
                return 'Unknown';
        }
    };

    const getFlagDescription = (employee) => {
        const { flag_reason, matching_pay_groups, employee_ctc } = employee;

        switch (flag_reason) {
            case 'conflict':
                const matchCount = matching_pay_groups?.length || 0;
                return `CTC ${employee_ctc?.toLocaleString()} matches ${matchCount} pay groups`;
            case 'no_match':
                return `CTC ${employee_ctc?.toLocaleString()} doesn't match any pay group range`;
            case 'out_of_range':
                return `CTC ${employee_ctc?.toLocaleString()} is outside all pay group ranges`;
            default:
                return 'Requires manual review';
        }
    };

    if (loading) {
        return (
            <div className={`p-8 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <div className="flex items-center justify-center gap-3">
                    <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
                    <span className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Loading flagged employees...
                    </span>
                </div>
            </div>
        );
    }

    if (flaggedEmployees.length === 0) {
        return (
            <div className={`p-8 rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                <div className="flex flex-col items-center justify-center gap-4 text-center">
                    <CheckCircle className="w-12 h-12 text-green-500" />
                    <div>
                        <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                            All Clear!
                        </h3>
                        <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            No employees require manual assignment review
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={`rounded-lg ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
                {/* Header */}
                <div className="p-6 border-b border-gray-700">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <AlertTriangle className="w-6 h-6 text-orange-500" />
                            <div>
                                <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                    Employees Requiring Manual Review
                                </h3>
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {flaggedEmployees.length} employee{flaggedEmployees.length !== 1 ? 's' : ''} need{flaggedEmployees.length === 1 ? 's' : ''} manual pay group assignment
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={fetchFlaggedEmployees}
                            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            <RefreshCw className="w-4 h-4" />
                            <span className="text-sm">Refresh</span>
                        </button>
                    </div>

                    {/* Summary Stats */}
                    {summary && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-2">
                                    <GitBranch className="w-4 h-4 text-orange-500" />
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Conflicts
                                    </span>
                                </div>
                                <p className={`mt-1 text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                    {summary.conflicts || 0}
                                </p>
                            </div>
                            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-2">
                                    <XCircle className="w-4 h-4 text-red-500" />
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        No Matches
                                    </span>
                                </div>
                                <p className={`mt-1 text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                    {summary.no_matches || 0}
                                </p>
                            </div>
                            <div className={`p-3 rounded-lg ${isDark ? 'bg-gray-700' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-2">
                                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        Out of Range
                                    </span>
                                </div>
                                <p className={`mt-1 text-2xl font-bold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                    {summary.out_of_range || 0}
                                </p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Flagged Employees List */}
                <div className="divide-y divide-gray-700">
                    {flaggedEmployees.map((employee) => (
                        <div
                            key={employee.employee_id}
                            className={`p-6 hover:bg-gray-700/50 transition-colors`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                {/* Employee Info */}
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-3">
                                        {getFlagIcon(employee.flag_reason)}
                                        <div>
                                            <h4 className={`font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                                                {employee.employee_name}
                                            </h4>
                                            <div className="flex items-center gap-4 mt-1">
                                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                    {employee.employee_code}
                                                </span>
                                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                                    employee.flag_reason === 'conflict'
                                                        ? 'bg-orange-100 text-orange-700'
                                                        : employee.flag_reason === 'no_match'
                                                        ? 'bg-red-100 text-red-700'
                                                        : 'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                    {getFlagLabel(employee.flag_reason)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                                        <div className="flex items-center gap-2">
                                            <Briefcase className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {employee.designation || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Building2 className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                            <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {employee.department_name || 'N/A'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <DollarSign className={`w-4 h-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                            <span className={`text-sm font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {employee.employee_ctc?.toLocaleString() || 'N/A'}
                                            </span>
                                        </div>
                                    </div>

                                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {getFlagDescription(employee)}
                                    </p>

                                    {/* Show matching pay groups for conflicts */}
                                    {employee.flag_reason === 'conflict' && employee.matching_pay_groups && employee.matching_pay_groups.length > 0 && (
                                        <div className="mt-3">
                                            <p className={`text-xs font-semibold mb-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                                Matching Pay Groups:
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {employee.matching_pay_groups.map((pg, idx) => (
                                                    <span
                                                        key={idx}
                                                        className={`px-3 py-1 rounded-full text-xs ${
                                                            isDark ? 'bg-blue-900/30 text-blue-300' : 'bg-blue-100 text-blue-700'
                                                        }`}
                                                    >
                                                        {pg.name} ({pg.min_ctc?.toLocaleString()} - {pg.max_ctc?.toLocaleString()})
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Action Button */}
                                <button
                                    onClick={() => handleResolveClick(employee)}
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors whitespace-nowrap"
                                >
                                    Assign Pay Group
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Resolution Modal */}
            {showResolutionModal && selectedEmployee && (
                <ConflictResolutionModal
                    employee={selectedEmployee}
                    onClose={() => setShowResolutionModal(false)}
                    onResolved={handleResolutionComplete}
                />
            )}
        </>
    );
};

// Placeholder for ConflictResolutionModal - will be implemented next
const ConflictResolutionModal = ({ employee, onClose, onResolved }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [availablePayGroups, setAvailablePayGroups] = useState([]);
    const [selectedPayGroup, setSelectedPayGroup] = useState(null);
    const [applySalaryStructure, setApplySalaryStructure] = useState(true);
    const [resolving, setResolving] = useState(false);

    useEffect(() => {
        fetchAvailablePayGroups();
    }, []);

    const fetchAvailablePayGroups = async () => {
        try {
            const response = await payGroupsService.getAllPayGroups({ is_active: true });
            if (response.success) {
                setAvailablePayGroups(response.pay_groups || []);

                // Pre-select if there are matching pay groups from conflict
                if (employee.matching_pay_groups && employee.matching_pay_groups.length > 0) {
                    setSelectedPayGroup(employee.matching_pay_groups[0].id);
                }
            }
        } catch (error) {
            console.error('Error fetching pay groups:', error);
        }
    };

    const handleResolve = async () => {
        if (!selectedPayGroup) {
            alert('Please select a pay group');
            return;
        }

        if (!window.confirm(`Assign ${employee.employee_name} to the selected pay group?`)) {
            return;
        }

        setResolving(true);
        try {
            const response = await payGroupsService.resolveAssignmentFlag(employee.employee_id, {
                pay_group_id: selectedPayGroup,
                apply_salary_structure: applySalaryStructure
            });

            if (response.success) {
                alert('Employee successfully assigned to pay group');
                onResolved();
            }
        } catch (error) {
            console.error('Error resolving flag:', error);
            alert(error.response?.data?.error || 'Failed to assign employee');
        } finally {
            setResolving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className={`w-full max-w-2xl rounded-lg shadow-xl ${isDark ? 'bg-gray-800' : 'bg-white'}`}>
                {/* Header */}
                <div className="p-6 border-b border-gray-700">
                    <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        Assign Pay Group - {employee.employee_name}
                    </h3>
                    <p className={`mt-1 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {employee.employee_code} • CTC: {employee.employee_ctc?.toLocaleString()}
                    </p>
                </div>

                {/* Body */}
                <div className="p-6 space-y-4">
                    {/* Pay Group Selection */}
                    <div>
                        <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Select Pay Group *
                        </label>
                        <select
                            value={selectedPayGroup || ''}
                            onChange={(e) => setSelectedPayGroup(parseInt(e.target.value))}
                            className={`w-full px-4 py-2 rounded-lg border ${
                                isDark
                                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                                    : 'bg-white border-gray-300 text-gray-900'
                            }`}
                        >
                            <option value="">-- Select Pay Group --</option>
                            {availablePayGroups.map((pg) => (
                                <option key={pg.id} value={pg.id}>
                                    {pg.name} ({pg.min_ctc?.toLocaleString()} - {pg.max_ctc?.toLocaleString()})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Apply Salary Structure Checkbox */}
                    <div className="flex items-center gap-3">
                        <input
                            type="checkbox"
                            id="applySalaryStructure"
                            checked={applySalaryStructure}
                            onChange={(e) => setApplySalaryStructure(e.target.checked)}
                            className="w-4 h-4 text-blue-600 rounded"
                        />
                        <label
                            htmlFor="applySalaryStructure"
                            className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}
                        >
                            Apply pay group's salary structure template to employee
                        </label>
                    </div>

                    {/* Warning for conflicts */}
                    {employee.flag_reason === 'conflict' && (
                        <div className="p-3 rounded-lg bg-orange-100 border border-orange-300">
                            <p className="text-sm text-orange-800">
                                <strong>Conflict:</strong> This employee's CTC matches multiple pay groups.
                                Please review and select the most appropriate one.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-gray-700 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        disabled={resolving}
                        className={`px-4 py-2 rounded-lg ${
                            isDark
                                ? 'bg-gray-700 hover:bg-gray-600 text-gray-100'
                                : 'bg-gray-200 hover:bg-gray-300 text-gray-900'
                        }`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleResolve}
                        disabled={resolving || !selectedPayGroup}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {resolving ? 'Assigning...' : 'Assign Pay Group'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default FlaggedEmployeesPanel;
